import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { deleteFilter } from 'api/Filter';

import { ia } from 'lib/helpers/utility';
import useModal from 'lib/hooks/useModal';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import Confirm from 'components/shared/Modal/Confirm/Confirm';
import ToggleSwitch from 'components/shared/ToggleSwitch/ToggleSwitch';
import SaveFilterModal from 'components/shared/Filters/components/SaveFilterModal';

import { useViewTabs } from '../hooks/useViewTabs';
import { useAutoTabSwitch } from '../hooks/useAutoTabSwitch';

const ViewTabs = ({
  items = [],
  category,
  filters,
  title,
  updateFilters,
  resetFilters,
  removeDefaults = false
}) => {
  const navigate = useNavigate();
  const [_, setSearchParams] = useSearchParams();
  const { isOpen, closeModal, openModal } = useModal();
  const { isOpen: isEditOpen, closeModal: closeEditModal, openModal: openEditModal } = useModal();

  const { view, activeItem, filtersData, toggleItems } = useViewTabs({
    title,
    items,
    category,
    updateFilters,
    resetFilters,
    removeDefaults,
    openModal,
    openEditModal
  });

  useAutoTabSwitch({ items: toggleItems, updateFilters });

  useEffect(() => {
    if (filters?.status && view) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        const currentView = newParams.get('view');
        const viewMatchesItem = items.some((_item) => _item.value === currentView);
        if (viewMatchesItem) {
          newParams.delete('view');
        }
        return newParams;
      });
    }
  }, [filters]);

  const { mutate: deleteFilterHandler, isLoading: isDeletingFilter } = useCustomMutation({
    mutationFn: (params) => deleteFilter(navigate, params),
    successMessage: 'Filter deleted',
    errorMessage: "Couldn't delete filter. Please try again.",
    invalidateQueryKey: ['default-filters', ['filters', category]],
    onSuccess: () => {
      resetFilters();
      setSearchParams((prev) => {
        const currentView = prev.get('view');
        if (currentView === isOpen.name) {
          prev.delete('view');
        }
        return prev;
      });
      closeModal();
    }
  });
  const handleDeleteFilter = () => deleteFilterHandler({ id: isOpen.id });

  if (!ia(toggleItems, 1)) return null;

  return (
    <>
      <div className="flex w-full min-w-0 gap-2">
        <ToggleSwitch items={toggleItems} active={activeItem?.value || view || null} />
      </div>

      {isOpen && (
        <Confirm
          isOpen={isOpen}
          variant="danger"
          handleClose={closeModal}
          title={`Delete view "${isOpen.name}"`}
          icon="trash-mono"
          message="You cannot do undo this action"
          footerActions={{
            primary: {
              text: 'Delete',
              onClick: handleDeleteFilter,
              color: 'danger',
              isLoading: isDeletingFilter
            }
          }}
        />
      )}
      {isEditOpen && (
        <SaveFilterModal
          title="Rename view"
          isOpen={isEditOpen}
          currentView={isEditOpen}
          onCancel={closeEditModal}
          filters={filters}
          category={category}
          currentViews={filtersData?.filters}
          items={items}
        />
      )}
    </>
  );
};

export default ViewTabs;
